/**
 * A ticket reservation holds tickets for a specified time period during the checkout process.
 *
 * When a site visitor selects tickets for an event, a ticket reservation is created to temporarily
 * reserve those tickets while the visitor completes their purchase. The reservation prevents other
 * visitors from purchasing the same tickets and includes pricing, seat information (if applicable),
 * and an expiration time.
 */
export interface TicketReservation {
  /**
   * Ticket reservation ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the ticket reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the ticket reservation.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the ticket reservation was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the ticket reservation was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Date and time when a `PENDING` ticket reservation will automatically expire if not confirmed.
   *
   * When a reservation expires, tickets are released back to the available inventory.
   * Expiration time is calculated based on the event's reservation duration settings. To set the reservation duration, call [Create Event](https://dev.wix.com/docs/api-reference/business-solutions/events/events-v3/create-event) API.
   *
   * **Note:** This field only applies to `PENDING` reservations.
   * @readonly
   */
  expirationDate?: Date | null;
  /**
   * Reservation status.
   * @readonly
   */
  status?: ReservationStatusWithLiterals;
  /**
   * Whether ticket reservation can be canceled.
   * @internal
   * @readonly
   */
  cancelable?: boolean | null;
  /**
   * ID of the site member who created this reservation.
   *
   * Only returned when the reservation was created by a logged-in site member.
   *
   * Learn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).
   * @format GUID
   * @readonly
   */
  memberId?: string | null;
  /**
   * ID of the site visitor who created this reservation.
   *
   * Returned for both logged-in members and anonymous visitors.
   *
   * Learn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).
   * @format GUID
   * @readonly
   */
  visitorId?: string | null;
  /**
   * List of ticket line items included in this reservation.
   *
   * Each line item represents tickets of the same type, pricing option, and seating arrangement.
   * Multiple line items are created when reserving different ticket types or seating sections.
   * @minSize 1
   * @maxSize 50
   */
  tickets?: TicketLineItem[];
  /**
   * Checkout type. Only tickets from events with the same checkout type can be added to a single reservation.
   * @internal
   * @readonly
   */
  checkoutType?: CheckoutTypeWithLiterals;
  /**
   * Advanced configuration options that modify default reservation behavior.
   *
   * Use these settings to override standard validation rules, such as availability limits or hidden ticket restrictions.
   * @immutable
   */
  additionalReservationSettings?: AdditionalReservationSettings;
  /**
   * Extended fields allow you to add custom properties beyond the standard reservation data.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions).
   */
  extendedFields?: ExtendedFields;
  /**
   * Tags allow you to classify ticket reservations.
   *
   * Learn more about [tags](https://dev.wix.com/docs/rest/business-management/tags/introduction).
   */
  tags?: Tags;
}

export enum ReservationStatus {
  /** Initial state when tickets are reserved. Will automatically expire after the expiration time if not confirmed. */
  PENDING = 'PENDING',
  /** Reservation is confirmed and tickets are secured. No expiration time applies. */
  CONFIRMED = 'CONFIRMED',
  /** Reservation was cancelled due to payment failure or system timeout, and tickets were released back to inventory. */
  CANCELED = 'CANCELED',
  /** Reservation was manually cancelled by the site owner, and tickets were released back to inventory. */
  CANCELED_MANUALLY = 'CANCELED_MANUALLY',
  /** Reservation exceeded its expiration time and tickets were released back to inventory. */
  EXPIRED = 'EXPIRED',
}

/** @enumType */
export type ReservationStatusWithLiterals =
  | ReservationStatus
  | 'PENDING'
  | 'CONFIRMED'
  | 'CANCELED'
  | 'CANCELED_MANUALLY'
  | 'EXPIRED';

export interface TicketLineItem {
  /**
   * Ticket line item ID.
   * @format GUID
   * @readonly
   */
  ticketLineItemId?: string | null;
  /**
   * ID of the event these tickets are for.
   * @format GUID
   * @readonly
   */
  eventId?: string | null;
  /**
   * ID of the [ticket definition](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-definitions-v3/introduction) being reserved.
   * @format GUID
   */
  ticketDefinitionId?: string | null;
  /**
   * Number of tickets to reserve for this line item.
   * @min 1
   * @max 50
   */
  quantity?: number | null;
  /** Detailed information about the tickets in this line item. */
  ticketInfo?: TicketInfo;
  /**
   * Snapshot of event configuration at the time of reservation creation.
   *
   * Preserves settings like reservation duration and ticket limits to ensure consistent behavior even if event settings change after reservation creation.
   * @readonly
   */
  eventSettingsSnapshot?: EventSettingsSnapshot;
  /**
   * Number of tickets that are removed from this line item after manually updating reservation in the dashboard.
   * @max 50
   * @readonly
   */
  removedQuantity?: number | null;
  /**
   * Terms and conditions that apply to this ticket type.
   * @minLength 1
   * @maxLength 50000
   * @readonly
   */
  policy?: string | null;
  /**
   * Display name of the ticket type for this line item.
   * @minLength 1
   * @maxLength 30
   * @readonly
   */
  ticketName?: string | null;
  /**
   * Price per individual ticket in this line item.
   *
   * Represents the base price for one ticket before any quantity calculations. This price includes any pricing option discounts but excludes service fees.
   * @readonly
   */
  price?: CommonMoney;
  /**
   * Total price for all tickets in this line item.
   *
   * Calculated as `price` multiplied by `quantity`. This represents the subtotal for this line item before taxes and service fees are applied.
   * @readonly
   */
  subTotal?: CommonMoney;
  /**
   * Wix service fee applied to the item `total` (or `discount.after_discount` if exists) + `tax.amount`.
   * @internal
   * @readonly
   */
  serviceFee?: ServiceFee;
  /**
   * Returned if reservation has checkout type of ECOMM_PLATFORM
   * @internal
   * @readonly
   */
  catalogReference?: CatalogReference;
}

export interface TicketInfo {
  /**
   * Unique identifier for the assigned seat at the event venue.
   *
   * Required for events with assigned seating. Each ticket in the line item can have a different seat assignment within the same section or area.
   * @minLength 1
   * @maxLength 36
   */
  seatId?: string | null;
  /**
   * Detailed seat location information.
   *
   * Includes section, row, seat number, and other venue-specific identifiers to help customers locate their assigned seats.
   * @readonly
   */
  seatInfo?: SeatInfo;
  /**
   * The guest enters the ticket price. This pricing method is applied to donation.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   * @immutable
   */
  guestPrice?: string | null;
  /**
   * ID of the pricing option selected for this ticket.
   * @format GUID
   */
  pricingOptionId?: string | null;
  /**
   * Display name of the selected pricing option.
   * @readonly
   * @maxLength 200
   */
  pricingOptionName?: string | null;
  /**
   * Ticket pricing type. Learn more about [pricing types](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-definitions-v3/ticket-definition-object).
   * @internal
   * @readonly
   */
  pricingType?: TypeWithLiterals;
  /**
   * Pricing plan benefits applied to this ticket.
   *
   * Contains information about subscription plan discounts or benefits that affect the ticket pricing for this customer.
   */
  pricingPlan?: PricingPlan;
  /**
   * Custom minimum guest price for tickets that support a guest price.
   * @internal
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  minGuestPrice?: string | null;
  /**
   * Override price that replaces the standard ticket price.
   *
   * Used for promotional pricing, staff discounts, or other special pricing that differs from the standard ticket definition price.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   * @immutable
   */
  priceOverride?: string | null;
}

export interface SeatInfo {
  /**
   * High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.
   * @readonly
   * @maxLength 20
   */
  sectionLabel?: string | null;
  /**
   * General admission or general seating area label.
   * @readonly
   * @maxLength 50
   */
  areaLabel?: string | null;
  /**
   * Table label for events with table seating.
   * @readonly
   * @maxLength 50
   */
  tableLabel?: string | null;
  /**
   * Row label within a section.
   * @readonly
   * @maxLength 50
   */
  rowLabel?: string | null;
  /**
   * Individual seat label within a row or at a table.
   * @readonly
   * @maxLength 4
   */
  seatLabel?: string | null;
  /**
   * Seating plan ID from the Wix Events & Tickets app.
   * @readonly
   * @format GUID
   */
  configurationId?: string | null;
}

export enum Type {
  /** Standard ticket pricing. */
  STANDARD = 'STANDARD',
  /** Donation ticket pricing. */
  DONATION = 'DONATION',
}

/** @enumType */
export type TypeWithLiterals = Type | 'STANDARD' | 'DONATION';

export interface PricingPlan {
  /**
   * Order ID of the pricing plan purchase.
   *
   * References the specific order through which the customer acquired their subscription plan benefits.
   * @format GUID
   */
  orderId?: string;
  /**
   * Unique identifier of the pricing plan.
   *
   * References the specific subscription plan that grants pricing benefits or discounts for this ticket reservation.
   * @format GUID
   */
  planId?: string;
}

export interface EventSettingsSnapshot {
  /**
   * Reservation duration in minutes.
   * @internal
   * @readonly
   * @min 5
   * @max 30
   */
  reservationDurationInMinutes?: number | null;
  /**
   * The default ticket stock value is as defined in the `TicketDefinition` object. If the ticket stock is defined in the stock manager app, the default is ignored.
   * @internal
   * @readonly
   * @format GUID
   */
  stockManagerAppId?: string | null;
  /**
   * Default: 20 tickets.
   * @internal
   * @readonly
   * @max 50
   */
  ticketLimitPerOrder?: number | null;
  /**
   * Checkout type.
   * @internal
   * @readonly
   */
  checkoutType?: CheckoutTypeWithLiterals;
}

export enum CheckoutType {
  /** Checkout using Events App. */
  EVENTS_APP = 'EVENTS_APP',
  /** Checkout using Ecomm Platform. */
  ECOMM_PLATFORM = 'ECOMM_PLATFORM',
}

/** @enumType */
export type CheckoutTypeWithLiterals =
  | CheckoutType
  | 'EVENTS_APP'
  | 'ECOMM_PLATFORM';

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface CommonMoney {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).
   * @format CURRENCY
   */
  currency?: string;
}

export interface ServiceFee {
  /**
   * How fee is calculated.
   * @readonly
   */
  type?: FeeTypeWithLiterals;
  /**
   * Fee rate.
   * @format DECIMAL_VALUE
   * @readonly
   * @decimalValue options { maxScale:2 }
   */
  rate?: string;
}

export enum FeeType {
  /** Seller absorbs the fee. It is deducted from the ticket price. */
  FEE_INCLUDED = 'FEE_INCLUDED',
  /** Fee is added to the ticket price at checkout. */
  FEE_ADDED_AT_CHECKOUT = 'FEE_ADDED_AT_CHECKOUT',
}

/** @enumType */
export type FeeTypeWithLiterals =
  | FeeType
  | 'FEE_INCLUDED'
  | 'FEE_ADDED_AT_CHECKOUT';

/** Used for grouping line items. Sent when an item is added to a cart, checkout, or order. */
export interface CatalogReference {
  /**
   * ID of the item within the catalog it belongs to.
   * @minLength 1
   * @maxLength 36
   */
  catalogItemId?: string;
  /**
   * ID of the app providing the catalog.
   *
   * You can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   *
   * For items from Wix catalogs, the following values always apply:
   * + Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`
   * + Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * + Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`
   * @minLength 1
   */
  appId?: string;
  /**
   * Additional item details in `key:value` pairs.
   *
   * Use this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.
   *
   * For Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)
   * or [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).
   */
  options?: Record<string, any> | null;
}

export interface AdditionalReservationSettings {
  /**
   * Whether to bypass ticket availability limits when creating the reservation.
   *
   * When `true`, allows creating reservations even if tickets appear sold out. Use with caution as this can lead to overselling. Typically used for admin purposes or special circumstances.
   */
  disableTicketLimitValidation?: boolean | null;
  /**
   * Whether to allow reservations for tickets marked as hidden.
   *
   * Hidden tickets are not displayed in the standard ticket selection on the site. Enable this to allow reservations for hidden tickets through direct API calls.
   */
  enableHiddenTicketReservation?: boolean | null;
  /**
   * Whether to exclude Wix service fees for this reservation.
   * @internal
   */
  disableWixFee?: boolean | null;
  /**
   * Whether to skip minimum price validation for guest-priced tickets.
   * @internal
   */
  disableGuestPriceValidation?: boolean | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface TicketReservationBalanceUpdated {
  /** Current event ticket reservation balance */
  balance?: TicketReservationBalance;
}

export interface TicketReservationBalance {
  /**
   * The unique identifier of the event
   * @format GUID
   */
  eventId?: string;
  /**
   * Collection of balance information for each ticket definition in the event
   * @maxSize 100
   */
  ticketDefinitionBalances?: TicketDefinitionBalance[];
  /**
   * Date and time when the balance record was created
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time when the balance record was last updated
   * @readonly
   */
  updatedDate?: Date | null;
}

/**
 * Represents the current state of reservations for a specific ticket definition.
 * Contains counts of confirmed tickets, pending tickets, and the total.
 */
export interface TicketDefinitionBalance {
  /**
   * The unique identifier of the ticket definition
   * @format GUID
   */
  ticketDefinitionId?: string;
  /** Number of confirmed tickets */
  confirmedTickets?: number;
  /** Number of tickets in pending state (reserved but not yet confirmed) */
  pendingTickets?: number;
  /** Total number of tickets (confirmed + pending) */
  totalTickets?: number;
  /**
   * Whether there exists a paid reservation
   * @internal
   */
  paidReservationExists?: boolean;
}

export interface CreateCheckoutRequest {
  /**
   * Ticket reservation ID to create checkout from.
   * @format GUID
   */
  ticketReservationId?: string | null;
}

export interface CreateCheckoutResponse {
  /**
   * Created checkout ID.
   * @format GUID
   */
  checkoutId?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface CreateTicketReservationRequest {
  /**
   * Ticket reservation details to create.
   *
   * Must include at least one ticket line item with valid ticket definition ID
   * and quantity. Additional settings like seat assignments and pricing options
   * can be specified in the ticket info.
   */
  ticketReservation: TicketReservation;
}

export interface CreateTicketReservationResponse {
  /** The created ticket reservation. */
  ticketReservation?: TicketReservation;
  /** Promotion-related errors that occurred during reservation creation. */
  promotionErrors?: PromotionErrors;
}

export interface PromotionErrors {
  /**
   * Errors.
   * @maxSize 10
   * @readonly
   */
  error?: Error[];
}

export enum PromotionType {
  /** Unknown promotion type. This value isn't used. */
  UNKNOWN_PROMOTION_TYPE = 'UNKNOWN_PROMOTION_TYPE',
  /** Error after failing to apply a promotion that doesn't fit into specific categories. */
  OTHER = 'OTHER',
  /** Error after failing to apply the coupon code. */
  COUPON_CODE = 'COUPON_CODE',
  /** Error after failing to apply the paid plan benefit. */
  PAID_PLAN_BENEFIT = 'PAID_PLAN_BENEFIT',
  /** Error after failing to apply the gift card. */
  GIFT_CARD = 'GIFT_CARD',
}

/** @enumType */
export type PromotionTypeWithLiterals =
  | PromotionType
  | 'UNKNOWN_PROMOTION_TYPE'
  | 'OTHER'
  | 'COUPON_CODE'
  | 'PAID_PLAN_BENEFIT'
  | 'GIFT_CARD';

export interface Error {
  /** Promotion type. */
  promotionType?: PromotionTypeWithLiterals;
  /**
   * Error code from the service provider.
   * @maxLength 50
   * @readonly
   */
  code?: string | null;
  /**
   * Additional error message.
   * @maxLength 200
   * @readonly
   */
  message?: string | null;
}

export interface GetTicketReservationRequest {
  /**
   * ID of the ticket reservation to retrieve.
   * @format GUID
   */
  ticketReservationId: string;
}

export interface GetTicketReservationResponse {
  /** The requested ticket reservation. */
  ticketReservation?: TicketReservation;
  /** Promotion-related errors associated with this reservation. */
  promotionErrors?: PromotionErrors;
}

export interface GetCurrentTicketReservationRequest {
  /**
   * Optional event id to retrieve current reservation in specific event context.
   * @internal
   * @format GUID
   */
  eventId?: string | null;
}

export interface GetCurrentTicketReservationResponse {
  /** The current visitor's TicketReservation. */
  ticketReservation?: TicketReservation;
  /** Promotions errors */
  promotionErrors?: PromotionErrors;
}

export interface UpdateTicketReservationRequest {
  /** TicketReservation to be updated, may be partial. */
  ticketReservation?: TicketReservation;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
  /** Updates reservation but does not save changes. */
  previewReservation?: boolean;
}

export interface UpdateTicketReservationResponse {
  /** Updated TicketReservation. */
  ticketReservation?: TicketReservation;
  /** Promotions errors */
  promotionErrors?: PromotionErrors;
}

export interface UpdateCurrentTicketReservationRequest {
  /** TicketReservation to be updated, may be partial. */
  ticketReservation?: TicketReservation;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
  /** Updates reservation but does not save changes. */
  previewReservation?: boolean;
}

export interface UpdateCurrentTicketReservationResponse {
  /** Updated TicketReservation. */
  ticketReservation?: TicketReservation;
  /** Promotions errors. */
  promotionErrors?: PromotionErrors;
}

export interface AddTicketsToCurrentTicketReservationRequest {
  /**
   * @minSize 1
   * @maxSize 50
   */
  tickets?: TicketLineItem[];
}

export interface AddTicketsToCurrentTicketReservationResponse {
  /** Updated TicketReservation. */
  ticketReservation?: TicketReservation;
}

export interface AddTicketsToTicketReservationRequest {
  /**
   * ID of the ticket reservation to add tickets to.
   * @format GUID
   */
  ticketReservationId?: string;
  /**
   * Tickets to add to a reservation.
   * @minSize 1
   * @maxSize 50
   */
  tickets?: TicketLineItem[];
}

export interface AddTicketsToTicketReservationResponse {
  /** Updated TicketReservation. */
  ticketReservation?: TicketReservation;
}

export interface RemoveTicketsFromCurrentTicketReservationRequest {
  /**
   * Ticket line item IDs.
   * @format GUID
   * @minSize 1
   * @maxSize 50
   */
  ticketLineItemIds?: string[] | null;
}

export interface RemoveTicketsFromCurrentTicketReservationResponse {
  /** Updated TicketReservation. */
  ticketReservation?: TicketReservation;
}

export interface RemoveTicketsFromTicketReservationRequest {
  /**
   * ID of the ticket reservation to add tickets to.
   * @format GUID
   */
  ticketReservationId?: string;
  /**
   * Ticket line item IDs.
   * @format GUID
   * @minSize 1
   * @maxSize 50
   */
  ticketLineItemIds?: string[] | null;
}

export interface RemoveTicketsFromTicketReservationResponse {
  /** Updated TicketReservation. */
  ticketReservation?: TicketReservation;
}

export interface DeleteTicketReservationRequest {
  /**
   * ID of the ticket reservation to delete.
   * @format GUID
   */
  ticketReservationId: string;
}

export interface DeleteTicketReservationResponse {
  /** Deleted ticket reservation. */
  ticketReservation?: TicketReservation;
}

export interface DeleteCurrentTicketReservationRequest {}

export interface DeleteCurrentTicketReservationResponse {
  /** Deleted TicketReservation. */
  ticketReservation?: TicketReservation;
}

export interface BulkUpdateTicketReservationTagsRequest {
  /**
   * List of ticket reservation IDs to update.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
  /** Tags to add to the specified reservations. */
  assignTags: Tags;
  /** Tags to remove from the specified reservations. */
  unassignTags?: Tags;
}

export interface BulkUpdateTicketReservationTagsResponse {
  /**
   * Results of the bulk tag update operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateTicketReservationTagsResult[];
  /** Metadata about the bulk update operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdateTicketReservationTagsResult {
  /** Metadata for the individual reservation update operation. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateTicketReservationTagsByFilterRequest {
  /** Filter criteria to select reservations for tag updates. */
  filter: Record<string, any> | null;
  /** Tags to add to matching reservations. */
  assignTags: Tags;
  /** Tags to remove from matching reservations. */
  unassignTags?: Tags;
}

export interface BulkUpdateTicketReservationTagsByFilterResponse {
  /**
   * Job ID for tracking the asynchronous operation.
   *
   * Use this ID to monitor the progress and status of the bulk tag update operation.
   * The operation runs in the background and may take time to complete for large datasets.
   * @format GUID
   */
  jobId?: string;
}

export interface ConfirmTicketReservationRequest {
  /**
   * ID of the TicketReservation to confirm.
   * @format GUID
   */
  ticketReservationId?: string;
}

export interface ConfirmTicketReservationResponse {
  /** The confirmed TicketReservation. */
  ticketReservation?: TicketReservation;
}

export interface CancelTicketReservationRequest {
  /**
   * ID of the ticket reservation to cancel.
   * @format GUID
   */
  ticketReservationId: string;
}

export interface CancelTicketReservationResponse {
  /** The canceled ticket reservation. */
  ticketReservation?: TicketReservation;
}

export interface CancelTicketReservationOnGracePeriodEndRequest {
  /**
   * ID of the TicketReservation to cancel.
   * @format GUID
   */
  ticketReservationId?: string;
}

export interface CancelTicketReservationOnGracePeriodEndResponse {}

export interface ExpireTicketReservationRequest {
  /**
   * ID of the TicketReservation to expire.
   * @format GUID
   */
  ticketReservationId?: string;
}

export interface ExpireTicketReservationResponse {
  /** The expired TicketReservation. */
  ticketReservation?: TicketReservation;
}

export interface UpdateTicketReservationCancelableRequest {
  /**
   * ID of the TicketReservation to update.
   * @format GUID
   */
  ticketReservationId?: string | null;
  /** Whether TicketReservation can be canceled. */
  cancelable?: boolean | null;
}

export interface UpdateTicketReservationCancelableResponse {}

export interface Empty {}

/** Triggered when an order is archived. */
export interface OrderArchived {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber?: string;
  /**
   * Reservation ID associated with the order.
   * @format GUID
   */
  reservationId?: string;
  /** Order status. */
  status?: OrderStatusWithLiterals;
  /**
   * Archived tickets.
   * @maxSize 50
   */
  tickets?: Ticket[];
}

export enum OrderStatus {
  /** Order status isn't available for this request fieldset. */
  NA_ORDER_STATUS = 'NA_ORDER_STATUS',
  /** Order is confirmed, no payment is required. */
  FREE = 'FREE',
  /** Order was paid, but the payment gateway suspended the payment. */
  PENDING = 'PENDING',
  /** Order is paid. */
  PAID = 'PAID',
  /** Order is confirmed but must be paid via offline payment. Status needs to be manually updated to `"PAID"`. */
  OFFLINE_PENDING = 'OFFLINE_PENDING',
  /** Order is waiting for payment at the cashier. */
  INITIATED = 'INITIATED',
  /** Order is canceled. */
  CANCELED = 'CANCELED',
  /** Order payment is declined. */
  DECLINED = 'DECLINED',
  /** Order payment is authorized. */
  AUTHORIZED = 'AUTHORIZED',
  /** Order payment is voided. */
  VOIDED = 'VOIDED',
  /** Order is partially paid with less than the total amount. */
  PARTIALLY_PAID = 'PARTIALLY_PAID',
}

/** @enumType */
export type OrderStatusWithLiterals =
  | OrderStatus
  | 'NA_ORDER_STATUS'
  | 'FREE'
  | 'PENDING'
  | 'PAID'
  | 'OFFLINE_PENDING'
  | 'INITIATED'
  | 'CANCELED'
  | 'DECLINED'
  | 'AUTHORIZED'
  | 'VOIDED'
  | 'PARTIALLY_PAID';

export interface Ticket {
  /** Unique issued ticket number. */
  ticketNumber?: string;
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId?: string;
  /** Ticket check-in. */
  checkIn?: CheckIn;
  /** Ticket price. */
  price?: Money;
  /** Whether ticket is archived. */
  archived?: boolean;
  /** Guest first name. */
  firstName?: string | null;
  /** Guest last name. */
  lastName?: string | null;
  /** Guest email. */
  email?: string | null;
  /**
   * Contact ID associated with this ticket.
   * @format GUID
   */
  contactId?: string | null;
  /** Whether ticket is confirmed */
  confirmed?: boolean;
  /**
   * Member ID associated with this ticket.
   * @format GUID
   */
  memberId?: string | null;
  /** Ticket form response (only assigned tickets contain separate forms). */
  form?: FormResponse;
  /** Ticket name. */
  ticketName?: string;
  /** Anonymized tickets no longer contain personally identifiable information (PII). */
  anonymized?: boolean;
  /** URL and password to online conference */
  onlineConferencingLogin?: OnlineConferencingLogin;
  /**
   * Seat ID associated with this ticket.
   * @maxLength 36
   */
  seatId?: string | null;
  /** Whether ticket is canceled. */
  canceled?: boolean | null;
  /**
   * Custom price of a ticket.
   * @internal
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   */
  priceOverride?: string | null;
  /**
   * Pricing option ID.
   * @internal
   * @format GUID
   */
  pricingOptionId?: string | null;
}

export interface CheckIn {
  /** Time of a ticket's check-in. */
  created?: Date | null;
  /**
   * Date and time the guest cancelled check-in.
   * @internal
   */
  checkInCanceledDate?: Date | null;
  /**
   * Id of the user that last performed check-in
   * @internal
   * @format GUID
   */
  checkedInBy?: string | null;
  /**
   * Id of the user that last performed check-in cancel
   * @internal
   * @format GUID
   */
  checkInCanceledBy?: string | null;
  /**
   * Id of the Staff Member who last performed check-in
   * @internal
   * @format GUID
   */
  checkedInByStaffMember?: string | null;
  /**
   * Id of theStaff Member who last canceled check-in
   * @internal
   * @format GUID
   */
  checkInCanceledByStaffMember?: string | null;
}

export interface Money {
  /**
   * *Deprecated:** Use `value` instead.
   * @format DECIMAL_VALUE
   * @deprecated
   */
  amount?: string;
  /**
   * 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string | null;
}

export interface FormResponse {
  /**
   * Form field inputs.
   * @maxSize 200
   */
  inputValues?: InputValue[];
}

export interface InputValue {
  /**
   * Form field input name.
   * @maxLength 100
   */
  inputName?: string;
  /**
   * Form field value.
   * @maxLength 5000
   */
  value?: string;
  /**
   * Form field values.
   * @maxSize 100
   * @maxLength 5000
   */
  values?: string[];
  /**
   * Integer or floating point number value.
   * @internal
   */
  number?: number | null;
  /**
   * UTC datetime in `YYYY-MM-DDThh:mm[:ss][.sss]Z` format.
   * @internal
   */
  dateTime?: Date | null;
  /**
   * Address type value.
   * @internal
   */
  address?: FormattedAddress;
}

export interface FormattedAddress {
  /**
   * 1-line address representation.
   * @maxLength 200
   */
  formatted?: string;
  /** Address components. */
  address?: Address;
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision shorthand. Usually, a short code (2 or 3 letters) that represents a state, region, prefecture, or province. e.g. NY */
  subdivision?: string | null;
  /**
   * Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2), e.g. US-NY
   * @internal
   * @maxLength 20
   */
  subdivisionIso31662?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
  /**
   * A string containing the full address of this location.
   * @internal
   */
  formattedAddress?: string | null;
  /**
   * Free text to help find the address.
   * @internal
   */
  hint?: string | null;
  /**
   * Coordinates of the physical address.
   * @internal
   */
  geocode?: AddressLocation;
  /**
   * Country full name.
   * @internal
   */
  countryFullname?: string | null;
  /**
   * Subdivision full name.
   * @internal
   */
  subdivisionFullname?: string | null;
  /**
   * Multi-level subdivisions from top to bottom.
   * @internal
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /**
   * Apartment number.
   * @internal
   */
  apt?: string;
  /**
   * Optional address line 1
   * @internal
   */
  formattedAddressLine?: string | null;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
  /**
   * Subdivision level
   * @internal
   */
  type?: SubdivisionTypeWithLiterals;
  /**
   * Free text description of subdivision type.
   * @internal
   */
  typeInfo?: string | null;
  /**
   * Standard organizations details (e.g. ISO).
   * @internal
   */
  standardDetails?: StandardDetails;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

/** Subdivision Concordance values */
export interface StandardDetails {
  /**
   * subdivision iso-3166-2 code according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). e.g. US-NY, GB-SCT, NO-30
   * @maxLength 20
   */
  iso31662?: string | null;
}

export interface OnlineConferencingLogin {
  /**
   * Link URL to the online conference.
   * @format WEB_URL
   * @readonly
   */
  link?: string;
  /**
   * Password for the online conference.
   * @readonly
   */
  password?: string | null;
}

/** Triggered when an order is unarchived. */
export interface OrderUnarchived {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber?: string;
  /**
   * Reservation ID associated with the order.
   * @format GUID
   */
  reservationId?: string;
  /** Order status. */
  status?: OrderStatusWithLiterals;
  /**
   * Unarchived tickets.
   * @maxSize 50
   */
  tickets?: Ticket[];
}

/** Triggered when a ticket in an order is canceled. */
export interface OrderTicketsCanceled {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber?: string;
  /**
   * Reservation ID associated with the order.
   * @format GUID
   */
  reservationId?: string;
  /** Order status. */
  status?: OrderStatusWithLiterals;
  /**
   * Only canceled tickets.
   * @maxSize 50
   */
  canceledTickets?: Ticket[];
}

export interface QueryEventsWithPaidReservationsRequest {
  /**
   * Event keys.
   * @minSize 1
   * @maxSize 100
   */
  eventKeys?: EventKey[];
}

/** Event key. */
export interface EventKey {
  /**
   * Instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
}

export interface QueryEventsWithPaidReservationsResponse {
  /**
   * Event IDs.
   * @format GUID
   * @maxSize 100
   */
  eventIds?: string[] | null;
}

export interface FixArchivedTicketReservationRequest {
  /**
   * ID of the ticket reservation to fix.
   * @format GUID
   */
  ticketReservationId?: string;
}

export interface FixArchivedTicketReservationResponse {
  /** The found ticket reservation. */
  ticketReservation?: TicketReservation;
}

/** @docsIgnore */
export type CreateTicketReservationApplicationErrors =
  | {
      code?: 'TICKET_DEFINITION_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'EVENT_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNSUPPORTED_PRICING_OPTION';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type GetTicketReservationApplicationErrors = {
  code?: 'TICKET_RESERVATION_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CancelTicketReservationApplicationErrors = {
  code?: 'TICKET_RESERVATION_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
